import React, { useRef, useEffect, useState, useCallback } from 'react';
import Icon from "../../elements/icon";
import Plus from "../../elements/plus";

const CloseIcon = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 1.5L8.5 8.5M8.5 1.5L1.5 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

const TabBar = ({ editDisabled, tabs, activeTab, onTabClick, onAddTab, onCloseTab, onSave, onReorderTabs }) => {
    const tabBarRef = useRef(null);
    const [dragIndex, setDragIndex] = useState(null);
    const dragOverIndex = useRef(null);

    useEffect(() => {
        if (tabBarRef.current) {
            tabBarRef.current.scrollTo({
                left: tabBarRef.current.scrollWidth,
                behavior: 'smooth',
            });
        }
    }, [tabs.length]);

    const handleDragStart = useCallback((e, index) => {
        setDragIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
        // Use a transparent drag image for cleaner look
        const dragGhost = e.target.cloneNode(true);
        dragGhost.style.position = 'absolute';
        dragGhost.style.top = '-1000px';
        dragGhost.style.opacity = '0.8';
        dragGhost.style.background = '#1a1a1a';
        dragGhost.style.borderRadius = '6px';
        dragGhost.style.padding = '0 12px';
        document.body.appendChild(dragGhost);
        e.dataTransfer.setDragImage(dragGhost, e.target.offsetWidth / 2, e.target.offsetHeight / 2);
        requestAnimationFrame(() => {
            document.body.removeChild(dragGhost);
        });
    }, []);

    const handleDragOver = useCallback((e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragIndex === null || index === dragIndex) return;
        // Live reorder: swap as soon as we hover a different tab
        if (dragOverIndex.current !== index) {
            dragOverIndex.current = index;
            onReorderTabs(dragIndex, index);
            setDragIndex(index);
        }
    }, [dragIndex, onReorderTabs]);

    const handleDragEnd = useCallback(() => {
        setDragIndex(null);
        dragOverIndex.current = null;
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
    }, []);

    return (
        <div className="tab-bar">
            <a href="/" className="logo-container" aria-label="Home">
                <Icon />
            </a>
            <span className="brand-name">pbin</span>
            <div className="divider" />
            <div className="tabs" ref={tabBarRef} onDrop={handleDrop}>
                {tabs.map((tab, index) => (
                    <div
                        key={index}
                        className={`tab${activeTab === index ? ' active' : ''}${dragIndex === index ? ' dragging' : ''}`}
                        onClick={() => onTabClick(index)}
                        draggable={!editDisabled}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                    >
                        <div className="tab-label">
                            <span>{tab.title || `Tab ${index + 1}`}</span>
                        </div>
                        {!editDisabled && (
                            <div className="tab-close" onClick={(e) => { e.stopPropagation(); onCloseTab(index); }}>
                                <CloseIcon />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {!editDisabled && (
                <>
                    <div className="add-tab-button" onClick={onAddTab} title="New Tab">
                        <Plus />
                    </div>
                    <div className="divider" />
                    <button className="save-button" onClick={onSave}>
                        Save
                        <span className="shortcut-badge">Ctrl+S</span>
                    </button>
                </>
            )}
        </div>
    );
};

export default TabBar;
